import { html, css, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { stringify } from "yaml";
import { PageElement } from "../lib/page-element.js";
import { shared } from "../styles.js";
import {
  getPackage,
  listVersions,
  listTags,
  resolve,
  search,
  download,
  tag as moveTag,
  yank,
  setVisibility,
  deprecatePackage,
  archivePackage,
  listResources,
  type PackageRecord,
  type VersionRecord,
  type TagRecord,
  type ResolveResponse,
  type SearchItem,
  type ResourceRecord,
} from "../api/registry.js";
import { humanError } from "../api/errors.js";
import { myRoleInTeam } from "../api/teams.js";
import { roleAtLeast, type Role } from "../lib/roles.js";
import { renderMarkdownSafe } from "../lib/markdown.js";
import { splitCsv, formatCount, relativeTime } from "../lib/format.js";
import { previewType } from "../lib/preview.js";
import { buildPullCommand } from "../lib/pull-command.js";
import { extractDatasetRequires } from "../lib/requires-labels.js";
import { readParamSpec } from "../lib/param-spec.js";
import { extractCompat, type CompatLabels } from "../lib/compat-labels.js";
import type { RequiresLabels } from "../lib/derive-requires.js";
import { loadEditor } from "../lib/load-editor.js";
import "../components/version-list.js";
import "../components/deps-panel.js";
import "../components/requires-panel.js";
import "../components/copy-pull.js";
import "../components/ibcs-preview.js";
import "../components/snapshot-preview.js";
import "../components/resources-panel.js";

type Tab = "readme" | "definition" | "dependencies" | "resources" | "settings";
type Visibility = "public" | "private";

/**
 * page-package is the package detail surface (R9/R10/R17/S2) — the design's
 * "Package Detail" screen. A 236px sidebar holds the add card, a meta list and the
 * version history; the main column holds identity, the install command, and a tabbed
 * body (Readme / Definition with the stored snapshot preview + YAML / Dependencies +
 * binding contract / Settings for maintainer+). All maintainer actions live in the
 * Settings tab: visibility, whole-package deprecate/archive, per-release
 * deactivate (yank), and the non-`latest` tag pointer; `latest` is system-managed
 * and the server is authoritative on permissions (forbidden is surfaced).
 */
@customElement("page-package")
export class PagePackage extends PageElement {
  static override styles = [
    shared,
    css`
      .frame {
        border: 1px solid var(--border);
        border-radius: 16px;
        overflow: hidden;
        background: var(--surface);
        display: grid;
        grid-template-columns: 236px 1fr;
      }
      @media (max-width: 820px) {
        .frame {
          grid-template-columns: 1fr;
        }
        aside {
          border-right: 0;
          border-bottom: 1px solid var(--border);
        }
      }
      aside {
        border-right: 1px solid var(--border);
        padding: 18px 16px;
        display: flex;
        flex-direction: column;
        gap: 18px;
      }
      .add-card {
        border: 1px solid var(--border);
        border-radius: 9px;
        padding: 13px;
      }
      .add-card .pv {
        height: 54px;
        margin-bottom: 11px;
      }
      .add-btn {
        width: 100%;
        height: 36px;
        border: 0;
        border-radius: 8px;
        background: var(--brand);
        color: var(--brand-contrast);
        font: 600 13px/1 var(--sans);
        cursor: pointer;
      }
      .meta {
        display: flex;
        flex-direction: column;
        gap: 11px;
        padding: 0 2px;
      }
      .meta .r {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      }
      .meta .k {
        font: 400 12px/1 var(--sans);
        color: var(--text-faint);
      }
      .meta .v {
        font: 500 12px/1 var(--mono);
        color: var(--text);
        text-align: right;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .meta .v.link {
        font-family: var(--sans);
        color: var(--brand-ink);
      }
      .versions {
        border-top: 1px solid var(--border);
        padding-top: 14px;
      }
      .versions .h {
        font: 600 10.5px/1 var(--mono);
        letter-spacing: 0.08em;
        color: var(--text-faint);
        margin-bottom: 10px;
      }
      .versions .list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .versions .vrow {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .versions .vrow a {
        font: 500 12px/1 var(--mono);
        color: var(--text-dim);
      }
      .versions .vrow:first-child a {
        color: var(--text);
      }
      .versions .vrow .when {
        font: 400 11px/1 var(--sans);
        color: var(--text-faint);
      }

      .main {
        padding: 24px;
        min-width: 0;
      }
      .title-row {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
        margin-bottom: 8px;
      }
      .title-row h1 {
        font: 600 23px/1 var(--mono);
        color: var(--text);
        margin: 0;
      }
      .vpill {
        font: 500 11px/1 var(--mono);
        color: var(--brand-ink);
        border: 1px solid var(--brand);
        border-radius: 6px;
        padding: 4px 8px;
      }
      .vis-badge {
        font: 600 10.5px/1 var(--sans);
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: var(--warning);
        border: 1px solid var(--warning);
        border-radius: 6px;
        padding: 4px 8px;
      }
      .subtitle {
        font: 400 14px/1.55 var(--sans);
        color: var(--text-dim);
        margin: 0 0 18px;
        max-width: 560px;
      }
      .keywords {
        margin: 0 0 18px;
      }
      .install {
        max-width: 480px;
        margin-bottom: 22px;
      }
      .tabs {
        display: flex;
        gap: 22px;
        border-bottom: 1px solid var(--border);
        margin-bottom: 18px;
      }
      .tab {
        font: 500 13px/1 var(--sans);
        color: var(--text-dim);
        padding-bottom: 10px;
        background: none;
        border: 0;
        border-bottom: 2px solid transparent;
        cursor: pointer;
      }
      .tab:hover {
        color: var(--text);
      }
      .tab.active {
        color: var(--text);
        font-weight: 600;
        border-bottom-color: var(--brand);
      }
      .readme :first-child {
        margin-top: 0;
      }
      .preview-panel {
        border: 1px solid var(--border);
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 22px;
      }
      .preview-panel .strip {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 9px 13px;
        border-bottom: 1px solid var(--border);
        background: var(--surface-2);
      }
      .preview-panel .strip .f {
        font: 500 11.5px/1 var(--mono);
        color: var(--text-dim);
      }
      .preview-panel .strip .m {
        font: 500 11px/1 var(--mono);
        color: var(--text-faint);
      }
      .preview-panel .body {
        padding: 20px 24px;
        height: 210px;
      }
      .yaml {
        white-space: pre;
      }
      yaml-editor {
        max-height: 70vh;
      }
      .row {
        display: flex;
        gap: var(--sp-2);
        align-items: flex-end;
        flex-wrap: wrap;
        margin-bottom: var(--sp-3);
      }
      .row > * {
        flex: 1;
        min-width: 120px;
      }
      .lc-msg {
        font-size: var(--fs-1);
        color: var(--text-faint);
        margin: 0 0 var(--sp-2);
      }
      .notice {
        border-color: var(--pos);
      }
      .vis-opt {
        display: flex;
        align-items: flex-start;
        gap: 9px;
        margin-bottom: var(--sp-2);
        font-size: var(--fs-2);
        font-weight: 400;
        color: var(--text);
        cursor: pointer;
      }
      .vis-opt input {
        width: auto;
        margin: 2px 0 0;
      }
      .vis-opt .d {
        color: var(--text-dim);
      }
    `,
  ];

  @property() scope = "";
  @property() name = "";

  @state() private pkg: PackageRecord | null = null;
  @state() private versions: VersionRecord[] = [];
  @state() private tags: TagRecord[] = [];
  @state() private resolved: ResolveResponse | null = null;
  @state() private stat: SearchItem | null = null;
  @state() private tab: Tab = "readme";
  @state() private addCopied = false;
  @state() private errorMsg = "";
  @state() private notice = "";
  @state() private loading = false;
  @state() private editorReady = false;
  @state() private defYaml = "";
  @state() private myRole: Role | null = null;
  @state() private resourceList: ResourceRecord[] = [];
  private defLoadedFor = "";
  private resourcesLoadedFor = "";
  private addCopiedTimer?: ReturnType<typeof setTimeout>;

  override disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.addCopiedTimer);
  }

  // maintainer-tools form state (tags & releases)
  @state() private newTagName = "";
  @state() private tagVersion = "";
  @state() private lifecycleVersion = "";
  @state() private lifecycleMsg = "";

  // settings-tab form state ("" = follow the record's current visibility)
  @state() private visChoice: Visibility | "" = "";
  @state() private savingVis = false;
  @state() private pkgDeprecateMsg = "";
  @state() private savingDeprecate = false;
  @state() private savingArchive = false;

  override willUpdate(changed: PropertyValues) {
    if (changed.has("scope") || changed.has("name")) {
      void this.load();
    }
  }

  override updated() {
    const version = this.resolved?.version ?? this.pkg?.expand?.latest_version?.version ?? "";
    if (this.tab === "definition" && this.pkg) {
      this.ensureDefinition(version);
    } else if (this.tab === "resources" && this.pkg) {
      this.ensureResources(version);
    }
  }

  /** ensureResources lazily loads the Resources tab's list (the latest
   * version's resources) the first time the tab opens, mirroring
   * ensureDefinition's lazy-load-on-tab-open pattern. */
  private ensureResources(version: string) {
    const key = `${this.scope}/${this.name}@${version}`;
    if (!version || this.resourcesLoadedFor === key) return;
    this.resourcesLoadedFor = key;
    void listResources(this.scope, this.name, version)
      .then((rows) => (this.resourceList = rows))
      .catch((e) => (this.errorMsg = humanError(e)));
  }

  /**
   * ensureDefinition lazily loads the Definition tab's YAML the first time the tab
   * opens: the exact frozen bytes via download() (what the digest covers), falling
   * back to re-stringifying the stored document on error, shown in the read-only
   * editor once its chunk arrives (<pre> until then).
   */
  private ensureDefinition(version: string) {
    const key = `${this.scope}/${this.name}@${version}`;
    if (!version || this.defLoadedFor === key) return;
    this.defLoadedFor = key;
    void loadEditor().then(() => (this.editorReady = true));
    void download(this.scope, this.name, version)
      .then(({ yaml }) => (this.defYaml = yaml))
      .catch(() => (this.defYaml = this.latestDocYaml(version)));
  }

  private async load() {
    if (!this.scope || !this.name) return;
    this.loading = true;
    this.errorMsg = "";
    this.visChoice = "";
    this.setTitle(`@${this.scope}/${this.name}`);
    try {
      const pkg = await getPackage(this.scope, this.name);
      this.pkg = pkg;
      const teamId = pkg.expand?.scope?.team ?? "";
      const [versions, tags, resolved, stat, myRole] = await Promise.all([
        listVersions(pkg.id),
        listTags(pkg.id),
        resolve(this.scope, this.name).catch(() => null),
        this.loadStat(),
        teamId ? myRoleInTeam(teamId).catch(() => null) : Promise.resolve(null),
      ]);
      this.versions = versions;
      this.tags = tags;
      this.resolved = resolved;
      this.stat = stat;
      this.myRole = myRole;
      this.tab = pkg.description ? "readme" : "definition";
      this.tagVersion = versions[0]?.version ?? "";
      this.lifecycleVersion = versions[0]?.id ?? "";
    } catch (e) {
      this.errorMsg = humanError(e);
      this.pkg = null;
    } finally {
      this.loading = false;
    }
  }

  /** loadStat fetches the search row for this exact package (pulls/updated/stable). */
  private async loadStat(): Promise<SearchItem | null> {
    try {
      const res = await search({ scope: [this.scope], q: this.name, perPage: 25, facets: false });
      return res.items.find((i) => i.name === this.name && i.scope === `@${this.scope}`) ?? null;
    } catch {
      return null;
    }
  }

  private labelsFor(version: string | undefined): Record<string, string> {
    const rec = this.versions.find((v) => v.version === version) ?? this.versions[0];
    return ((rec?.document as { metadata?: { labels?: Record<string, string> } })?.metadata
      ?.labels ?? {}) as Record<string, string>;
  }

  private paramsFor(version: string | undefined): unknown {
    const rec = this.versions.find((v) => v.version === version) ?? this.versions[0];
    return (rec?.document as { metadata?: { params?: unknown } })?.metadata?.params;
  }

  private requiresFor(version: string | undefined): RequiresLabels {
    return {
      datasets: extractDatasetRequires(this.labelsFor(version)),
      params: readParamSpec(this.paramsFor(version)),
    };
  }

  private compatFor(version: string | undefined): CompatLabels {
    return extractCompat(this.labelsFor(version));
  }

  private latestDocYaml(version: string | undefined): string {
    const rec = this.versions.find((v) => v.version === version) ?? this.versions[0];
    if (!rec?.document || typeof rec.document !== "object") return "";
    try {
      return stringify(rec.document);
    } catch {
      return "";
    }
  }

  private async copyAdd(coord: string) {
    try {
      await navigator.clipboard.writeText(buildPullCommand(coord, "latest"));
      this.addCopied = true;
      clearTimeout(this.addCopiedTimer);
      this.addCopiedTimer = setTimeout(() => (this.addCopied = false), 1500);
    } catch {
      /* clipboard may be blocked */
    }
  }

  private async setTag() {
    if (!this.pkg) return;
    this.errorMsg = "";
    this.notice = "";
    if (this.newTagName.trim().toLowerCase() === "latest") {
      this.errorMsg = "`latest` is system-managed and cannot be moved manually.";
      return;
    }
    try {
      await moveTag(this.pkg.id, this.newTagName.trim(), this.tagVersion);
      this.notice = `Tag “${this.newTagName.trim()}” set to ${this.tagVersion}.`;
      this.newTagName = "";
      this.tags = await listTags(this.pkg.id);
    } catch (e) {
      this.errorMsg = humanError(e);
    }
  }

  /**
   * saveVisibility flips the package's row-level visibility (maintainer+):
   * optimistic flip, PATCH settings, rollback + error banner on failure.
   */
  private async saveVisibility() {
    const pkg = this.pkg;
    if (!pkg) return;
    const prev: Visibility = pkg.visibility === "private" ? "private" : "public";
    const next: Visibility = this.visChoice || prev;
    if (next === prev) return;
    this.errorMsg = "";
    this.notice = "";
    this.savingVis = true;
    this.pkg = { ...pkg, visibility: next };
    try {
      await setVisibility(this.scope, this.name, next);
      this.notice = `Visibility set to ${next}.`;
    } catch (e) {
      this.pkg = { ...pkg, visibility: prev };
      this.visChoice = prev;
      this.errorMsg = humanError(e);
    } finally {
      this.savingVis = false;
    }
  }

  /** deactivateRelease yanks/unyanks the selected version — excluded from tag-follow
   *  resolution and downloads, but still pullable by exact pin. */
  private async deactivateRelease(undo: boolean) {
    if (!this.lifecycleVersion) return;
    this.errorMsg = "";
    this.notice = "";
    try {
      await yank(this.lifecycleVersion, this.lifecycleMsg, undo);
      this.notice = `Release ${undo ? "reactivated" : "deactivated"}.`;
      this.lifecycleMsg = "";
      if (this.pkg) this.versions = await listVersions(this.pkg.id);
    } catch (e) {
      this.errorMsg = humanError(e);
    }
  }

  /**
   * deprecatePackage marks/clears deprecation on the whole package (all versions,
   * maintainer+): optimistic flip, POST deprecate, rollback + error banner on failure.
   */
  private async savePackageDeprecation(undo: boolean) {
    const pkg = this.pkg;
    if (!pkg) return;
    this.errorMsg = "";
    this.notice = "";
    this.savingDeprecate = true;
    const prev = { deprecated: pkg.deprecated, deprecatedMsg: pkg.deprecatedMsg };
    this.pkg = { ...pkg, deprecated: !undo, deprecatedMsg: undo ? "" : this.pkgDeprecateMsg };
    try {
      await deprecatePackage(this.scope, this.name, this.pkgDeprecateMsg, undo);
      this.notice = `Package ${undo ? "undeprecated" : "deprecated"}.`;
      if (!undo) this.pkgDeprecateMsg = "";
    } catch (e) {
      this.pkg = { ...pkg, ...prev };
      this.errorMsg = humanError(e);
    } finally {
      this.savingDeprecate = false;
    }
  }

  /**
   * savePackageArchive soft-removes/restores the whole package (maintainer+):
   * optimistic flip, POST archive, rollback + error banner on failure.
   */
  private async savePackageArchive(undo: boolean) {
    const pkg = this.pkg;
    if (!pkg) return;
    this.errorMsg = "";
    this.notice = "";
    this.savingArchive = true;
    this.pkg = { ...pkg, archived: !undo };
    try {
      await archivePackage(this.scope, this.name, undo);
      this.notice = `Package ${undo ? "restored" : "removed"}.`;
    } catch (e) {
      this.pkg = { ...pkg, archived: pkg.archived };
      this.errorMsg = humanError(e);
    } finally {
      this.savingArchive = false;
    }
  }

  override render() {
    if (this.loading && !this.pkg) return html`<div class="wrap"><h1>Loading…</h1></div>`;
    if (!this.pkg) {
      return html`<div class="wrap">
        <h1>Package</h1>
        <div class="error" role="alert">${this.errorMsg || "Not found."}</div>
      </div>`;
    }
    const pkg = this.pkg;
    const team = pkg.expand?.scope?.expand?.team;
    const ref = this.resolved?.tag ?? "latest";
    const version = this.resolved?.version ?? pkg.expand?.latest_version?.version ?? "";
    const coord = `@${this.scope}/${this.name}`;
    const pv = previewType(pkg.kind, pkg.category);
    const keywords = splitCsv(pkg.tags);
    const compat = this.compatFor(version);

    return html`
      <div class="wrap">
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        ${this.notice ? html`<div class="card notice">${this.notice}</div>` : ""}

        <div class="frame">
          <aside>
            <div class="add-card">
              <div class="pv"><ibcs-preview type=${pv}></ibcs-preview></div>
              <button class="add-btn" aria-label="Add to project" @click=${() => this.copyAdd(coord)}>
                ${this.addCopied ? "Copied ✓" : "Add to project"}
              </button>
            </div>
            <div class="meta">
              ${version ? this.metaRow("Version", html`<span class="v mono">${version}</span>`) : ""}
              ${this.stat
                ? this.metaRow("Installs", html`<span class="v">${formatCount(this.stat.pulls30d)}</span>`)
                : ""}
              ${this.metaRow("Kind", html`<span class="v">${pkg.kind}</span>`)}
              ${pkg.visibility === "private"
                ? this.metaRow("Visibility", html`<span class="v">private</span>`)
                : ""}
              ${compat.engine
                ? this.metaRow("Engine", html`<span class="v" title=${compat.engine}>${compat.engine}</span>`)
                : ""}
              ${compat.cli
                ? this.metaRow("CLI", html`<span class="v" title=${compat.cli}>${compat.cli}</span>`)
                : ""}
              ${this.metaRow(
                "Publisher",
                team
                  ? html`<a class="v link" href=${`/teams/${team.name}`}>${team.display_name || team.name}</a>`
                  : html`<a class="v link" href=${`/scopes/${this.scope}`}>@${this.scope}</a>`,
              )}
              ${this.versions[0]
                ? this.metaRow(
                    "Updated",
                    html`<span class="v">${relativeTime(this.versions[0].created)}</span>`,
                  )
                : ""}
            </div>
            ${this.versions.length ? this.renderVersions() : ""}
          </aside>

          <div class="main">
            <div class="title-row">
              <h1>${coord}</h1>
              ${version ? html`<span class="vpill">v${version}</span>` : ""}
              ${pkg.visibility === "private" ? html`<span class="vis-badge">Private</span>` : ""}
            </div>
            ${pkg.title ? html`<p class="subtitle">${pkg.title}</p>` : ""}
            ${keywords.length
              ? html`<div class="keywords">${keywords.map((k) => html`<span class="chip">${k}</span>`)}</div>`
              : ""}

            <div class="install"><copy-pull .pkg=${coord} .ref=${ref}></copy-pull></div>

            <div class="tabs" role="tablist" aria-label="Component detail">
              ${this.tabBtn("readme", "Readme")} ${this.tabBtn("definition", "Definition")}
              ${this.tabBtn("dependencies", "Dependencies")} ${this.tabBtn("resources", "Resources")}
              ${roleAtLeast(this.myRole, "maintainer") ? this.tabBtn("settings", "Settings") : ""}
            </div>
            <div role="tabpanel" id="tabpanel" aria-labelledby=${`tab-${this.tab}`} tabindex="0">
              ${this.renderTabBody(version, pv)}
            </div>

            <h2 style="margin-top:26px">Version history</h2>
            <version-list
              .versions=${this.versions}
              .tags=${this.tags}
              .scope=${this.scope}
              .name=${this.name}
            ></version-list>
          </div>
        </div>
      </div>
    `;
  }

  private metaRow(k: string, v: unknown) {
    return html`<div class="r"><span class="k">${k}</span>${v}</div>`;
  }

  private tabBtn(tab: Tab, label: string) {
    return html`<button
      class="tab ${this.tab === tab ? "active" : ""}"
      role="tab"
      id=${`tab-${tab}`}
      aria-controls="tabpanel"
      aria-selected=${this.tab === tab ? "true" : "false"}
      @click=${() => (this.tab = tab)}
    >
      ${label}
    </button>`;
  }

  private renderVersions() {
    return html`<div class="versions">
      <div class="h">VERSIONS</div>
      <div class="list">
        ${this.versions.slice(0, 6).map(
          (v) => html`<div class="vrow">
            <a href=${`/p/${this.scope}/${this.name}/v/${v.version}`}>${v.version}</a>
            <span class="when">${relativeTime(v.created)}</span>
          </div>`,
        )}
      </div>
    </div>`;
  }

  private renderTabBody(version: string, pv: ReturnType<typeof previewType>) {
    if (this.tab === "readme") {
      return this.pkg?.description
        ? html`<div class="readme">${unsafeHTML(renderMarkdownSafe(this.pkg.description))}</div>`
        : html`<p class="muted">No readme provided for this component.</p>`;
    }
    if (this.tab === "definition") {
      const yaml = this.defYaml || this.latestDocYaml(version);
      return html`
        <div class="preview-panel">
          <div class="strip">
            <span class="f">${this.name}.yml</span>
            <span class="m">${this.pkg?.category || this.pkg?.kind || ""}</span>
          </div>
          <div class="body">
            <snapshot-preview
              .scope=${this.scope}
              .name=${this.name}
              .version=${version}
              .fallbackType=${pv}
            ></snapshot-preview>
          </div>
        </div>
        ${yaml
          ? this.editorReady && this.defYaml
            ? html`<yaml-editor .value=${this.defYaml} readOnly></yaml-editor>`
            : html`<pre class="yaml">${yaml}</pre>`
          : html`<p class="muted">No document available.</p>`}
      `;
    }
    if (this.tab === "settings") return this.renderSettings();
    if (this.tab === "resources") {
      return html`<resources-panel
        .resources=${this.resourceList}
        .scope=${this.scope}
        .name=${this.name}
        .version=${version}
      ></resources-panel>`;
    }
    // dependencies
    return html`
      <h3>Dependency closure</h3>
      <deps-panel .deps=${this.resolved?.dependencies ?? []}></deps-panel>
      <h3 style="margin-top:18px">Binding contract</h3>
      <requires-panel .requires=${this.requiresFor(version)}></requires-panel>
    `;
  }

  private renderSettings() {
    const pkg = this.pkg;
    const current: Visibility = pkg?.visibility === "private" ? "private" : "public";
    const choice: Visibility = this.visChoice || current;
    const opt = (value: Visibility, label: string, desc: string) => html`
      <label class="vis-opt">
        <input
          type="radio"
          name="visibility"
          value=${value}
          .checked=${choice === value}
          @change=${() => (this.visChoice = value)}
        />
        <span><strong>${label}</strong> — <span class="d">${desc}</span></span>
      </label>
    `;
    return html`
      <h3>Visibility</h3>
      <p class="muted">
        Visibility applies to this package's listing, search and downloads; document
        contents are unchanged. Published versions stay immutable either way.
      </p>
      ${opt("public", "Public", "anyone can find, view and pull this package.")}
      ${opt("private", "Private", "only members of the owning team can see or pull it; everyone else gets a 404.")}
      <button
        class="btn btn-primary"
        ?disabled=${choice === current || this.savingVis}
        @click=${this.saveVisibility}
      >
        ${this.savingVis ? "Saving…" : "Save"}
      </button>

      <h3 style="margin-top:26px">Package status</h3>
      <p class="muted">
        Deprecate or remove the whole package, all versions. Hard delete isn't
        offered — removing hides it from search and blocks downloads while
        keeping version history for audit.
      </p>
      ${pkg?.deprecated
        ? html`<p class="lc-msg">deprecated${pkg.deprecatedMsg ? `: ${pkg.deprecatedMsg}` : ""}</p>`
        : ""}
      <div class="row">
        <div>
          <label>Deprecation message</label>
          <input
            .value=${this.pkgDeprecateMsg}
            placeholder="reason / successor package"
            @input=${(e: Event) => (this.pkgDeprecateMsg = (e.target as HTMLInputElement).value)}
          />
        </div>
      </div>
      <div class="row">
        <button
          class="btn"
          ?disabled=${this.savingDeprecate}
          @click=${() => this.savePackageDeprecation(false)}
        >
          ${pkg?.deprecated ? "Update deprecation" : "Deprecate package"}
        </button>
        ${pkg?.deprecated
          ? html`<button
              class="btn"
              ?disabled=${this.savingDeprecate}
              @click=${() => this.savePackageDeprecation(true)}
            >
              Undeprecate package
            </button>`
          : ""}
      </div>

      ${pkg?.archived
        ? html`<p class="lc-msg">removed — hidden from search and blocked from downloads</p>`
        : ""}
      <div class="row">
        <button
          class="btn ${pkg?.archived ? "" : "btn-danger"}"
          ?disabled=${this.savingArchive}
          @click=${() => this.savePackageArchive(!!pkg?.archived)}
        >
          ${this.savingArchive ? "Saving…" : pkg?.archived ? "Restore package" : "Remove package"}
        </button>
      </div>

      ${this.renderMaintainerTools()}
    `;
  }

  private renderMaintainerTools() {
    const sel = this.versions.find((v) => v.id === this.lifecycleVersion);
    return html`
      <h3 style="margin-top:26px">Tags &amp; releases</h3>
      <p class="muted">
        <code>latest</code> is system-managed and cannot be moved manually.
      </p>
      <div class="row">
        <div>
          <label>Tag name</label>
          <input
            .value=${this.newTagName}
            placeholder="stable"
            @input=${(e: Event) => (this.newTagName = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div>
          <label>Target version</label>
          <select @change=${(e: Event) => (this.tagVersion = (e.target as HTMLSelectElement).value)}>
            ${this.versions.map(
              (v) => html`<option value=${v.version} ?selected=${v.version === this.tagVersion}>${v.version}</option>`,
            )}
          </select>
        </div>
        <div style="flex:0">
          <button class="btn btn-primary" @click=${this.setTag}>Set tag</button>
        </div>
      </div>
      <div class="row">
        <div>
          <label>Version</label>
          <select @change=${(e: Event) => (this.lifecycleVersion = (e.target as HTMLSelectElement).value)}>
            ${this.versions.map(
              (v) => html`<option value=${v.id} ?selected=${v.id === this.lifecycleVersion}>${v.version}</option>`,
            )}
          </select>
        </div>
        <div>
          <label>Message</label>
          <input
            .value=${this.lifecycleMsg}
            placeholder="reason / guidance"
            @input=${(e: Event) => (this.lifecycleMsg = (e.target as HTMLInputElement).value)}
          />
        </div>
      </div>
      ${sel?.deprecated && sel.deprecatedMsg
        ? html`<p class="lc-msg">deprecated: ${sel.deprecatedMsg}</p>`
        : ""}
      ${sel?.yanked && sel.yankedMsg
        ? html`<p class="lc-msg">deactivated: ${sel.yankedMsg}</p>`
        : ""}
      <div class="row">
        <button class="btn btn-danger" @click=${() => this.deactivateRelease(false)}>Deactivate release</button>
        <button class="btn" @click=${() => this.deactivateRelease(true)}>Reactivate release</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-package": PagePackage;
  }
}
